// src/pages/Staff.tsx
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import {
  Search,
  Plus,
  LayoutGrid,
  BarChart3,
} from 'lucide-react';

import StaffCard from '../components/staff/StaffCard';
import NewStaffModal from '../components/modals/NewStaffModal';
import PrimaryButton from '../components/ui/PrimaryButton';
import AnalyticsDateSelector from '../components/analytics/AnalyticsDateSelector';
import { useUsers } from '../contexts/UsersContext';
import { hasStaffInsights } from '@/lib/planFeatures';
import { CompensationsProvider } from '../contexts/CompensationsContext';
import StaffDetailModal from '../components/modals/StaffDetailModal';
import StaffPerformanceOverview from '../components/staff/StaffPerformanceOverview';

// Tipo staff locale
export interface StaffMember { // Esportiamola se serve altrove
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: 'Admin' | 'Manager' | 'Staff';
  isEmailVerified: boolean;
  avatarColor: string;
  avatar: string | null;
  isInvite?: boolean;
}

// Palette colori avatar
const AVATAR_COLORS = [
  'bg-clip-100 text-clip-600',
  'bg-purple-100 text-purple-600',
  'bg-blue-100 text-blue-600',
  'bg-gray-100 text-gray-600',
];

const getAvatarColor = (seed: string | number) => {
  const str = String(seed);
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash + str.charCodeAt(i) * 17) % 997;
  }
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

const Staff: React.FC = () => {
  const { t } = useTranslation();
  const { users, invites } = useUsers();
  const { isAdmin, isOwner, subscriptionPlan } = useAuth();

  // Performance e compensi: dal piano AI in su (oltre che solo titolare)
  const canSeeInsights = isOwner && hasStaffInsights(subscriptionPlan);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStaff, setSelectedStaff] = useState<StaffMember | null>(null);
  const [isNewStaffModalOpen, setIsNewStaffModalOpen] = useState(false);
  const [view, setView] = useState<'cards' | 'performance'>('cards');
  const [perfPeriod, setPerfPeriod] = useState('month');

  // Staff "veri" (utenti registrati)
  const userStaffMembers: StaffMember[] = useMemo(() => {
    if (!users) return [];

    return users
      .filter((u: any) => u.is_active !== false)
      .map((u: any): StaffMember => {
        const firstName = u.first_name || '';
        const lastName = u.last_name || '';
        const roleBackend = (u.role || '').toLowerCase();

        let role: StaffMember['role'];
        if (roleBackend === 'admin') role = 'Admin';
        else if (roleBackend === 'manager') role = 'Manager';
        else role = 'Staff';

        return {
          id: String(u.id),
          firstName: firstName || t('staff.defaults.first_name'),
          lastName: lastName || t('staff.defaults.last_name'),
          email: u.email || '',
          role,
          isEmailVerified: !!u.email,
          avatarColor: getAvatarColor(u.id),
          avatar: u.avatar || null, // <--- Mappiamo l'avatar dal DB
        };
      });
  }, [users, t]);

  // Inviti pendenti
  const inviteStaffMembers: StaffMember[] = useMemo(() => {
    if (!invites) return [];

    const now = new Date();

    return invites
      .filter((inv: any) => !inv.used && now < new Date(inv.expires_at))
      .map((inv: any): StaffMember => {
        const email: string = inv.email || '';
        const [localPart] = email.split('@');
        const displayName = localPart || t('staff.defaults.guest');

        return {
          id: `invite-${inv.token}`,
          firstName: displayName,
          lastName: '',
          email,
          role: 'Staff',
          isEmailVerified: false,
          avatarColor: getAvatarColor(email || inv.token),
          avatar: null, // Gli inviti non hanno ancora avatar
          isInvite: true,
        };
      });
  }, [invites, t]);

  // Lista combinata
  const allStaff: StaffMember[] = useMemo(
    () => [...userStaffMembers, ...inviteStaffMembers],
    [userStaffMembers, inviteStaffMembers]
  );

  // Filter Logic
  const filteredStaff = useMemo(
    () =>
      allStaff.filter((s) => {
        const term = searchTerm.toLowerCase();
        return (
          s.firstName.toLowerCase().includes(term) ||
          s.lastName.toLowerCase().includes(term) ||
          s.email.toLowerCase().includes(term)
        );
      }),
    [allStaff, searchTerm]
  );

  return (
    <CompensationsProvider>
    <div className="max-w-7xl mx-auto space-y-8 relative">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('staff.title')}</h1>
          <p className="text-gray-500 mt-1">{t('staff.subtitle')}</p>
        </div>

        <div className="flex items-center gap-3 w-full md:w-auto">
          {canSeeInsights && view === 'performance' && (
            <AnalyticsDateSelector value={perfPeriod} onChange={setPerfPeriod} />
          )}
          {view === 'cards' && (
            <div className="relative flex-1 md:w-80 group">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-clip-500 transition-colors"
                size={20}
              />
              <input
                type="text"
                placeholder={t('common.search') as string}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-clip-200 focus:border-clip-400 transition-all shadow-sm placeholder:text-gray-400"
              />
            </div>
          )}

          {isAdmin && view === 'cards' && (
            <PrimaryButton
              label={t('staff.new_staff')}
              icon={Plus}
              variant="primary"
              // className="bg-clip-900 hover:bg-black shadow-clip-400/50 text-white"
              onClick={() => setIsNewStaffModalOpen(true)}
            />
          )}
        </div>
      </div>

      {/* Toggle vista (solo titolare, dal piano AI): schede anagrafica vs panoramica economica */}
      {canSeeInsights && (
        <div className="bg-white rounded-2xl p-1.5 border border-gray-100 shadow-sm inline-flex gap-1">
          <button
            onClick={() => setView('cards')}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all ${view === 'cards' ? 'bg-clip-500 text-white shadow-md shadow-clip-200' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'}`}
          >
            <LayoutGrid size={16} /> Schede
          </button>
          <button
            onClick={() => setView('performance')}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all ${view === 'performance' ? 'bg-clip-500 text-white shadow-md shadow-clip-200' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'}`}
          >
            <BarChart3 size={16} /> Performance
          </button>
        </div>
      )}

      {/* Contenuto: panoramica economica (owner, piano AI) o griglia schede */}
      {canSeeInsights && view === 'performance' ? (
        <StaffPerformanceOverview period={perfPeriod} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredStaff.map((staff) => (
            <StaffCard
              key={staff.id}
              staff={staff}
              onClick={() => {
                if (staff.isInvite) return;
                setSelectedStaff(staff);
              }}
            />
          ))}
        </div>
      )}

      {/* Staff Detail Modal */}
      {selectedStaff && !selectedStaff.isInvite && (
        <StaffDetailModal
          staff={selectedStaff}
          isOpen={!!selectedStaff}
          onClose={() => setSelectedStaff(null)}
        />
      )}

      {/* New Staff Invite Modal */}
      <NewStaffModal
        isOpen={isNewStaffModalOpen}
        onClose={() => setIsNewStaffModalOpen(false)}
      />
    </div>
    </CompensationsProvider>
  );
};

export default Staff;